import { useDatoClient } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useEffect, useState } from 'react';

const logger = LogManager.getLogger('hook:useClipAssignees');

export type ClipAssignee = { id?: string; email: string; name?: string };

/**
 * Promise cache keyed by `${storyId}:${showcaseId}` so the showcase allowList is
 * fetched once per Content Studio tab rather than once per `VideoClipCard`. All
 * cards mounting together await the same in-flight request. The key includes the
 * story/showcase id, so navigating to a different story fetches afresh.
 */
const cache = new Map<string, Promise<ClipAssignee[]>>();

/**
 * Returns the people on the showcase's `allowList` (the assignable members) for
 * the currently loaded story's showcase, as `{ email, name }`. Returns an empty
 * list until loaded or when there is no story/showcase context.
 */
export function useClipAssignees(): ClipAssignee[] {
  const datoClientStore = useDatoClient();
  const videoCreator = useVideoCreatorStore();
  const storyId = videoCreator.story?.id;
  const showcaseId = videoCreator.organization?.id;
  const [assignees, setAssignees] = useState<ClipAssignee[]>([]);

  useEffect(() => {
    if (!storyId) {
      setAssignees([]);
      return;
    }

    const key = `${storyId}:${showcaseId ?? ''}`;
    let cancelled = false;

    let request = cache.get(key);
    if (!request) {
      const load = async (): Promise<ClipAssignee[]> => {
        try {
          const albums =
            await datoClientStore.albumRepository?.findAllWithStoryAccess(
              storyId,
            );
          const showcase =
            albums?.find(a => a.id === showcaseId) ?? albums?.[0];
          return showcase?.allowList ?? [];
        } catch (err) {
          // Drop the failed promise so a later mount can retry.
          cache.delete(key);
          logger.warn('Failed to load clip assignees', extractErrorInfo(err));
          return [];
        }
      };
      request = load();
      cache.set(key, request);
    }

    void request.then(list => {
      if (!cancelled) setAssignees(list);
    });

    return () => {
      cancelled = true;
    };
  }, [storyId, showcaseId, datoClientStore]);

  return assignees;
}
